import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";

// Simple hash for basic obfuscation (not cryptographic security)
function simpleHash(pin: string): string {
  let hash = 0;
  for (let i = 0; i < pin.length; i++) {
    const char = pin.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return hash.toString(36);
}

interface FrontDeskContextType {
  isFrontDeskMode: boolean;
  activateFrontDesk: () => void;
  deactivateFrontDesk: (pin: string) => boolean;
  frontDeskPin: string | null;
  setFrontDeskPin: (pin: string) => void;
  hasPinSet: boolean;
}

const FrontDeskContext = createContext<FrontDeskContextType | undefined>(undefined);

// Routes allowed in front desk mode
export const FRONT_DESK_ALLOWED_ROUTES = [
  "/appointments",
  "/clients",
  "/consultations",
  "/consent-forms",
  "/pos",
  "/client-check-in",
  "/walk-in-check-in",
];

export function FrontDeskProvider({ children }: { children: ReactNode }) {
  const { currentShopId } = useAuth();
  const pinKey = currentShopId ? `frontDesk_pin_${currentShopId}` : null;
  const modeKey = "frontDesk_active";

  const [isFrontDeskMode, setIsFrontDeskMode] = useState(() => {
    return sessionStorage.getItem(modeKey) === "true";
  });

  const [pinHash, setPinHash] = useState<string | null>(() => {
    return pinKey ? localStorage.getItem(pinKey) : null;
  });

  // Sync pin when shop changes
  useEffect(() => {
    if (pinKey) {
      setPinHash(localStorage.getItem(pinKey));
    }
  }, [pinKey]);

  const setFrontDeskPin = useCallback((pin: string) => {
    if (!pinKey) return;
    const hashed = simpleHash(pin);
    localStorage.setItem(pinKey, hashed);
    setPinHash(hashed);
  }, [pinKey]);

  const activateFrontDesk = useCallback(() => {
    sessionStorage.setItem(modeKey, "true");
    setIsFrontDeskMode(true);
  }, []);

  const deactivateFrontDesk = useCallback((pin: string) => {
    if (!pinHash) return false;
    if (simpleHash(pin) === pinHash) {
      sessionStorage.removeItem(modeKey);
      setIsFrontDeskMode(false);
      return true;
    }
    return false;
  }, [pinHash]);

  return (
    <FrontDeskContext.Provider value={{
      isFrontDeskMode,
      activateFrontDesk,
      deactivateFrontDesk,
      frontDeskPin: pinHash,
      setFrontDeskPin,
      hasPinSet: !!pinHash,
    }}>
      {children}
    </FrontDeskContext.Provider>
  );
}

export function useFrontDesk() {
  const ctx = useContext(FrontDeskContext);
  if (!ctx) throw new Error("useFrontDesk must be used within FrontDeskProvider");
  return ctx;
}
